import { useId, useRef, useState } from 'react';
import { CATALOG, FORECAST_BY_ID, SALES_BY_ID } from '../models/engine/pmfEngine';
import './ServicePreview.css';

const products = CATALOG.products;
const formatNumber = (number) => Math.round(number).toLocaleString('ko-KR');
const forecasts = products.map((product) => FORECAST_BY_ID[product.product_id]);
const totalStock = products.reduce((total, product) => total + product.stock, 0);
const totalDemand = forecasts.reduce((total, forecast) => total + forecast.p50, 0);
const replenishCount = forecasts.filter((forecast) => ['부족', '주의'].includes(forecast.status)).length;
const totalSpend = products.reduce((total, product) => total + product.ad_metrics.spend, 0);
const totalPurchases = products.reduce((total, product) => total + product.ad_metrics.purchase, 0);
const averageRoas = products.reduce((total, product) => total + product.ad_metrics.spend * product.ad_metrics.roas, 0) / totalSpend;
const channels = [...new Set(products.map((product) => product.channel))].map((channel) => {
  const items = products.filter((product) => product.channel === channel);
  const spend = items.reduce((total, product) => total + product.ad_metrics.spend, 0);
  const revenue = items.reduce((total, product) => total + product.ad_metrics.spend * product.ad_metrics.roas, 0);
  return { name: channel, roas: revenue / spend };
});

function DemandChart({ id }) {
  const history = SALES_BY_ID[products[0].product_id].slice(-14).map((day, index) => ({
    date: day.date,
    value: products.reduce((total, product) => total + SALES_BY_ID[product.product_id].slice(-14)[index].units, 0),
  }));
  const future = totalDemand / forecasts[0].horizon;
  const ceiling = Math.ceil(Math.max(...history.map((day) => day.value), future) / 50) * 50;
  const x = (index) => 35 + (index / 20) * 404;
  const y = (value) => 77 - (value / ceiling) * 66;
  const line = history.map((day, index) => `${x(index)},${y(day.value)}`).join(' ');
  const forecastLine = `${x(13)},${y(history[13].value)} ${x(14)},${y(future)} ${x(20)},${y(future)}`;
  const area = `M ${x(0)},77 L ${line.split(' ').join(' L ')} L ${x(13)},77 Z`;
  const dateLabel = (date) => date.slice(5).replace('-', '.');

  return (
    <svg className="service-preview-chart" viewBox="0 0 466 100" role="img" aria-label="최근 14일 판매량과 4주 전망의 일평균 예상 수요를 보여주는 모의 데이터 차트">
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#4e6cff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#4e6cff" stopOpacity="0.01" />
        </linearGradient>
        <clipPath id={`${id}-forecast-reveal`}>
          <rect className="service-preview-forecast-reveal" x={x(13) - 4} y="0" width={447 - x(13)} height="85" />
        </clipPath>
      </defs>
      <rect x={x(13)} y="5" width={439 - x(13)} height="72" rx="4" fill="#fff8ee" />
      {[ceiling, ceiling / 2, 0].map((value) => (
        <g key={value}>
          <line x1="35" x2="439" y1={y(value)} y2={y(value)} stroke="#e9edf4" strokeDasharray="3 4" />
          <text x="25" y={y(value) + 3} textAnchor="end">{value}</text>
        </g>
      ))}
      <path className="service-preview-demand-area" d={area} fill={`url(#${id}-fill)`} />
      <polyline className="service-preview-demand-line" pathLength="1" points={line} fill="none" stroke="#4e6cff" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={forecastLine} clipPath={`url(#${id}-forecast-reveal)`} fill="none" stroke="#f0a04b" strokeWidth="2.5" strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" />
      <circle className="service-preview-demand-point" cx={x(13)} cy={y(history[13].value)} r="3.5" fill="#fff" stroke="#4e6cff" strokeWidth="2" />
      <text x="35" y="96">{dateLabel(history[0].date)}</text>
      <text x={x(13)} y="96" textAnchor="middle">{dateLabel(history[13].date)}</text>
      <text x="439" y="96" textAnchor="end">향후 일평균</text>
    </svg>
  );
}

function AdsChart() {
  return (
    <svg className="service-preview-chart" viewBox="0 0 466 100" role="img" aria-label={`모의 데이터의 채널별 광고 수익률: ${channels.map((channel) => `${channel.name} ${channel.roas.toFixed(2)}배`).join(', ')}`}>
      {[0, 1, 2, 3, 4].map((value) => (
        <g key={value}>
          <line x1={83 + value * 83} x2={83 + value * 83} y1="5" y2="77" stroke="#e9edf4" strokeDasharray="3 4" />
          <text x={83 + value * 83} y="96" textAnchor="middle">{value}배</text>
        </g>
      ))}
      {channels.map((channel, index) => (
        <g key={channel.name} style={{ '--channel-delay': `${index * 110}ms` }}>
          <text x="0" y={28 + index * 36}>{channel.name}</text>
          <rect className="service-preview-ad-bar" x="83" y={10 + index * 36} width={channel.roas * 83} height="26" rx="5" fill={index === 0 ? '#4e6cff' : '#a9b7ff'} />
          <text className="service-preview-bar-value" x={94 + channel.roas * 83} y={28 + index * 36}>{channel.roas.toFixed(2)}</text>
        </g>
      ))}
    </svg>
  );
}

export default function ServicePreview() {
  const [tab, setTab] = useState('inventory');
  const id = useId();
  const tabRefs = useRef([]);
  const inventory = tab === 'inventory';
  const metrics = inventory ? [
    { label: '전체 재고', value: formatNumber(totalStock), unit: '개' },
    { label: '4주 예상 수요', value: formatNumber(totalDemand), unit: '개' },
    { label: '보충 검토 상품', value: replenishCount, unit: '개', accent: true },
  ] : [
    { label: '광고비', value: formatNumber(totalSpend), unit: '원' },
    { label: '광고 수익률', value: averageRoas.toFixed(2), unit: '배' },
    { label: '광고 구매', value: totalPurchases, unit: '건', accent: true },
  ];

  const handleTabKey = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
    setTab(nextIndex === 0 ? 'inventory' : 'advertising');
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <div className="service-preview">
      <div className="service-preview-topbar">
        <div className="service-preview-breadcrumb"><span className="service-preview-app-icon" aria-hidden="true">m</span><span>워크스페이스 <span className="service-preview-divider">/</span> <strong>운영 현황</strong></span></div>
        <span className="service-preview-example"><span aria-hidden="true" />예시 데이터</span>
      </div>
      <div className="service-preview-body">
        <div className="service-preview-toolbar">
          <div className="service-preview-tabs" role="tablist" aria-label="서비스 미리보기">
            {[['inventory', '재고 관리'], ['advertising', '광고 운영']].map(([key, label], index) => (
              <button key={key} ref={(element) => { tabRefs.current[index] = element; }} type="button" role="tab" id={`${id}-${key}-tab`} aria-selected={tab === key} aria-controls={`${id}-panel`} tabIndex={tab === key ? 0 : -1} onClick={() => setTab(key)} onKeyDown={(event) => handleTabKey(event, index)}>{label}</button>
            ))}
          </div>
          <span className="service-preview-date">{CATALOG.as_of.slice(5).replace('-', '.')} 기준</span>
        </div>
        <div key={tab} className="service-preview-panel" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}-tab`}>
          <div className="service-preview-metrics">
            {metrics.map((metric) => (
              <div className={`service-preview-metric${metric.accent ? ' service-preview-metric-accent' : ''}`} key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}<small>{metric.unit}</small></strong>
              </div>
            ))}
          </div>
          <div className="service-preview-chart-header">
            <strong>{inventory ? '판매 흐름과 수요 전망' : '채널별 광고 수익률'}</strong>
            {inventory ? <div className="service-preview-legend"><span>판매량</span><span>예상 수요</span></div> : <span className="service-preview-chart-detail">광고 매출 ÷ 광고비</span>}
          </div>
          {inventory ? <DemandChart id={id} /> : <AdsChart />}
          <div className="service-preview-products">
            <div className="service-preview-products-heading"><span>상품별 {inventory ? '재고' : '광고'} 현황</span><span>{inventory ? '현재 재고 · 상태' : '광고 수익률'}</span></div>
            {products.slice(0, 2).map((product, index) => {
              const forecast = FORECAST_BY_ID[product.product_id];
              return (
                <div className="service-preview-product" key={product.product_id}>
                  <span className={`service-preview-product-icon service-preview-product-icon-${index}`} aria-hidden="true"><span /></span>
                  <span className="service-preview-product-name">{product.product_name}</span>
                  {inventory ? <><span className="service-preview-stock">{formatNumber(product.stock)}개</span><span className={`service-preview-status${forecast.status === '적정' ? '' : ' service-preview-status-attention'}`}>{forecast.status}</span></> : <span className="service-preview-roas">{product.ad_metrics.roas.toFixed(1)}<small>배</small></span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="service-preview-footnote"><span aria-hidden="true">◈</span> 서비스 이해를 위한 모의 데이터입니다.</div>
    </div>
  );
}
